import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { TIMER_CONFIG } from '../config/constants';

const FlipClockTimer = ({ onTimerEnd }) => {
  // Store the total time in seconds instead of separate hour/minute/second states
  const [totalSeconds, setTotalSeconds] = useState(TIMER_CONFIG.DEFAULT_SECONDS); // Default 30 minutes
  const [isRunning, setIsRunning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // For editing mode
  const [editHours, setEditHours] = useState(0);
  const [editMinutes, setEditMinutes] = useState(TIMER_CONFIG.DEFAULT_MINUTES);
  const [editSeconds, setEditSeconds] = useState(0);
  
  const intervalRef = useRef(null);
  
  // Calculate hours, minutes, seconds from total seconds
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Format numbers to always have two digits
  const formatNumber = (num) => {
    return num.toString().padStart(2, '0');
  };

  // Effect for running the timer
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTotalSeconds(prevTotal => {
          if (prevTotal <= 1) {
            // Timer is ending
            clearInterval(intervalRef.current);
            setIsRunning(false);
            if (onTimerEnd) {
              setTimeout(() => onTimerEnd(), 0);
            }
            return 0;
          }
          return prevTotal - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, onTimerEnd]);

  // Initialize edit fields when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditHours(hours);
      setEditMinutes(minutes);
      setEditSeconds(seconds);
    }
  }, [isEditing, hours, minutes, seconds]);

  // Start timer
  const startTimer = () => {
    if (totalSeconds <= 0) return;
    setIsRunning(true);
    setIsEditing(false);
  };

  // Pause timer
  const pauseTimer = () => {
    setIsRunning(false);
  };

  // Reset timer to default (30 minutes)
  const resetTimer = () => {
    setIsRunning(false);
    setTotalSeconds(TIMER_CONFIG.DEFAULT_SECONDS);
    setIsEditing(false);
  };

  // Toggle edit mode
  const toggleEdit = () => {
    if (isRunning) return;
    
    if (isEditing) {
      // Apply changes when exiting edit mode
      const newTotalSeconds = 
        (editHours * 3600) + (editMinutes * 60) + editSeconds;
      setTotalSeconds(newTotalSeconds);
    }
    
    setIsEditing(!isEditing);
  };

  // Handle input changes in edit mode
  const handleInputChange = (e, setter, max) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= TIMER_CONFIG.MIN_VALUE) {
      setter(Math.min(value, max));
    }
  };

  return (
    <section className="timer">
      <div className="timer-container">
        <h3 className="timer-title">Session Timer</h3>
        
        <div className="timer-display">
          {isEditing ? (
            <div className="timer-edit-container">
              <input
                type="number"
                value={editHours}
                onChange={(e) => handleInputChange(e, setEditHours, TIMER_CONFIG.MAX_HOURS)}
                className="timer-input"
                min="0"
                max={TIMER_CONFIG.MAX_HOURS}
              />
              <span className="timer-separator">:</span>
              <input
                type="number"
                value={editMinutes}
                onChange={(e) => handleInputChange(e, setEditMinutes, TIMER_CONFIG.MAX_MINUTES)}
                className="timer-input"
                min="0"
                max={TIMER_CONFIG.MAX_MINUTES}
              />
              <span className="timer-separator">:</span>
              <input
                type="number"
                value={editSeconds}
                onChange={(e) => handleInputChange(e, setEditSeconds, TIMER_CONFIG.MAX_SECONDS)}
                className="timer-input"
                min="0"
                max={TIMER_CONFIG.MAX_SECONDS}
              />
            </div>
          ) : (
            <div className="flip-clock">
              <div className="flip-clock-digit">{formatNumber(hours)}</div>
              <div className="flip-clock-separator">:</div>
              <div className="flip-clock-digit">{formatNumber(minutes)}</div>
              <div className="flip-clock-separator">:</div>
              <div className="flip-clock-digit">{formatNumber(seconds)}</div>
            </div>
          )}
        </div>
        
        <div className="timer-controls">
          {!isRunning ? (
            <button
              onClick={startTimer}
              className="timer-button timer-start"
              disabled={totalSeconds <= 0}
            >
              Start
            </button>
          ) : (
            <button
              onClick={pauseTimer}
              className="timer-button timer-pause"
            >
              Pause
            </button>
          )}
          
          <button
            onClick={resetTimer}
            className="timer-button timer-reset"
          >
            Reset
          </button>
          
          <button
            onClick={toggleEdit}
            disabled={isRunning}
            className={`timer-button timer-edit ${isEditing ? 'active' : ''} ${isRunning ? 'disabled' : ''}`}
          >
            {isEditing ? 'Done' : 'Edit'}
          </button>
        </div>
      </div>
    </section>
  );
};

FlipClockTimer.propTypes = {
  onTimerEnd: PropTypes.func
};

export default FlipClockTimer;